import React, { useState, useEffect } from 'react';
import { useProjects } from '../contexts/ProjectContext';
import { Info, ChevronDown } from 'lucide-react';
import api from '../utils/apiClient';

const LinkingConfiguration = () => {
  const { currentProject } = useProjects();
  const [linkSource, setLinkSource] = useState('sitemap');
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [blogRootUrl, setBlogRootUrl] = useState('');
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detectedLinks, setDetectedLinks] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [linksAlreadyDetected, setLinksAlreadyDetected] = useState(false);

  // Helper function to normalize URLs
  const normalizeUrl = (url) => {
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = `https://${normalizedUrl}`;
    }
    return normalizedUrl;
  };

  // Load existing data from project
  useEffect(() => {
    const loadBlogInfo = async () => {
      if (!currentProject?.id) return;
      
      try {
        setLoading(true);
        const res = await api.projects.getBlogInfo(currentProject.id);
        const info = res.data || {};
        setSitemapUrl(info.sitemap_url || '');
        setBlogRootUrl(info.blog_root_url || '');
        
        // Check if there are already detected links
        if (info.sitemap_url) {
          try {
            // This is just a quick check to see if we have any links already
            const { data } = await api.projects.detectLinks(currentProject.id, {
              source: 'sitemap',
              url: info.sitemap_url,
              checkOnly: true
            });
            
            if (data && data.urls && data.urls.length > 0) {
              setLinksAlreadyDetected(true);
              setDetectedLinks(data.urls.map(url => ({
                url,
                title: url.split('/').pop().replace(/-/g, ' ').replace(/\.(html|php|aspx)$/, '')
              })));
            }
          } catch (err) {
            // Error handled silently
          }
        }
      } catch (err) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    
    loadBlogInfo();
  }, [currentProject?.id]);

  const handleDetectLinks = async () => {
    if (!sitemapUrl && linkSource === 'sitemap') {
      setError('Please enter a sitemap URL');
      return;
    }

    if (!blogRootUrl && linkSource === 'blog') {
      setError('Please enter a blog root URL');
      return;
    }

    // Validate sitemap URL format if using sitemap source
    if (linkSource === 'sitemap') {
      const normalizedUrl = normalizeUrl(sitemapUrl);
      
      // Check if it's likely a sitemap URL
      const isSitemapUrl = normalizedUrl.endsWith('.xml') || 
                           normalizedUrl.includes('sitemap') || 
                           normalizedUrl.includes('Sitemap');
      
      if (!isSitemapUrl) {
        setError('The URL doesn\'t appear to be a sitemap. Sitemap URLs typically end with .xml or contain "sitemap" in the URL.');
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Call the API to detect links
      const url = linkSource === 'sitemap' ? normalizeUrl(sitemapUrl) : normalizeUrl(blogRootUrl);
      const { data } = await api.projects.detectLinks(currentProject.id, {
        source: linkSource,
        url
      });
      
      // Transform the data into the format we need
      if (data && data.urls && Array.isArray(data.urls)) {
        const links = data.urls.map(url => ({
          url,
          title: url.split('/').pop().replace(/-/g, ' ').replace(/\.(html|php|aspx)$/, '')
        }));
        setDetectedLinks(links);
        
        // Set success message
        if (links.length > 0) {
          setSuccessMessage(`Successfully detected ${links.length} links from your ${linkSource === 'sitemap' ? 'sitemap' : 'blog'}.`);
          setLinksAlreadyDetected(true);
        } else {
          // No links were found, provide a more helpful message
          if (linkSource === 'sitemap') {
            setSuccessMessage(`No links were found in your sitemap. Please verify that your sitemap URL is correct and contains <url> tags with <loc> elements.`);
          } else {
            setSuccessMessage(`No links were found in your blog. Please check the URL and try again.`);
          }
        }
      } else {
        setDetectedLinks([]);
        setSuccessMessage(`No links were found in your ${linkSource === 'sitemap' ? 'sitemap' : 'blog'}. Please check the URL and try again.`);
      }
    } catch (err) {
      // Handle specific error cases
      const errorMessage = err.response?.data?.error || err.message || 'Failed to detect links';
      
      if (errorMessage.includes('ENOTFOUND') || errorMessage.includes('404')) {
        setError(`Could not find the ${linkSource === 'sitemap' ? 'sitemap' : 'blog'} at the specified URL. Please check the URL and try again.`);
      } else if (errorMessage.includes('timeout')) {
        setError(`The request timed out. This might happen with large sitemaps. Try using a more specific sitemap URL (e.g., a section-specific sitemap like /post-sitemap.xml instead of the main sitemap).`);
      } else if (errorMessage.includes('certificate')) {
        setError(`SSL certificate error. The website might have an invalid or expired SSL certificate.`);
      } else if (errorMessage.includes('maxContentLength') || errorMessage.includes('maxBodyLength')) {
        setError(`The sitemap file is too large to process. Try using a more specific sitemap URL for a section of your site.`);
      } else {
        setError(`Failed to detect links: ${errorMessage}. Please check your URL and try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Linking Configuration</h1>
      <p className="text-gray-600 mb-8">Configure how we find links on your website for internal linking and backlink exchange.</p>
      
      {linksAlreadyDetected && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                Links have been automatically detected from your sitemap. You can refresh the links or modify your settings below.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Column - Source Configuration */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <div className="bg-violet-100 p-2 rounded-lg mr-3">
              <div className="text-violet-600 w-5 h-5">🔗</div>
            </div>
            <h2 className="text-lg font-semibold">Source Configuration</h2>
            <div className="ml-2 text-gray-400 cursor-pointer hover:text-gray-600">
              <Info size={16} />
            </div>
          </div>
          
          {/* Choose Link Source Dropdown */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Choose Link Source
            </label>
            <div className="relative">
              <button
                onClick={() => setShowSourceDropdown(!showSourceDropdown)}
                className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <span>{linkSource === 'sitemap' ? 'Sitemap' : 'Blog Root URL'}</span>
                <ChevronDown size={16} className={`transition-transform ${showSourceDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showSourceDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg border border-gray-200 rounded-md py-1">
                  <button
                    onClick={() => {
                      setLinkSource('sitemap');
                      setShowSourceDropdown(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm ${linkSource === 'sitemap' ? 'bg-primary-50 text-primary-600' : 'hover:bg-gray-100'}`}
                  >
                    Sitemap
                  </button>
                  <button
                    onClick={() => {
                      setLinkSource('blog');
                      setShowSourceDropdown(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm ${linkSource === 'blog' ? 'bg-primary-50 text-primary-600' : 'hover:bg-gray-100'}`}
                  >
                    Blog Root URL
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Sitemap URL Input */}
          {linkSource === 'sitemap' && (
            <div className="mb-6">
              <label htmlFor="sitemap-url" className="block text-sm font-medium text-gray-700 mb-1">
                Sitemap URL
              </label>
              <input
                type="url"
                id="sitemap-url"
                value={sitemapUrl}
                onChange={(e) => setSitemapUrl(e.target.value)}
                placeholder="https://yourblog.com/sitemap.xml"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}
          
          {/* Blog Root URL Input */}
          {linkSource === 'blog' && (
            <div className="mb-6">
              <label htmlFor="blog-root-url" className="block text-sm font-medium text-gray-700 mb-1">
                Blog Root URL
              </label>
              <input
                type="url"
                id="blog-root-url"
                value={blogRootUrl}
                onChange={(e) => setBlogRootUrl(e.target.value)}
                placeholder="https://yourblog.com/blog"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}
          
          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Info size={20} className="text-blue-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  We scan your {linkSource === 'sitemap' ? 'sitemap URL' : 'blog root URL'} (and any nested {linkSource === 'sitemap' ? 'sitemaps' : 'pages'}) to find articles for linking.
                  This works best if you have a {linkSource === 'sitemap' ? 'sitemap' : 'blog structure'} that is updated regularly.
                </p>
              </div>
            </div>
          </div>
          
          {/* Detect Links Button */}
          <button
            onClick={handleDetectLinks}
            disabled={loading}
            className="w-full py-2 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                <span>Detecting Links...</span>
              </div>
            ) : (
              'Detect Links'
            )}
          </button>
          
          {error && (
            <div className="mt-4 bg-red-50 border border-red-100 text-red-700 p-3 rounded-md">
              {error}
            </div>
          )}
          
          {successMessage && !error && (
            <div className="mt-4 bg-green-50 border border-green-100 text-green-700 p-3 rounded-md">
              {successMessage}
            </div>
          )}
        </div>
        
        {/* Right Column - Detected Links */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <div className="bg-green-100 p-2 rounded-lg mr-3">
              <div className="text-green-600 w-5 h-5">✓</div>
            </div>
            <h2 className="text-lg font-semibold">
              Detected Links {detectedLinks.length > 0 && `(${detectedLinks.length})`}
            </h2>
            <div className="ml-2 text-gray-400 cursor-pointer hover:text-gray-600">
              <Info size={16} />
            </div>
          </div>
          
          {detectedLinks.length > 0 ? (
            <div className="space-y-4">
              {detectedLinks.slice(0, 50).map((link, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-3 hover:bg-gray-50">
                  <h3 className="font-medium text-gray-800">{link.title}</h3>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                    {link.url}
                  </a>
                </div>
              ))}
              {detectedLinks.length > 50 && (
                <div className="text-center text-gray-500 py-2">
                  Showing 50 of {detectedLinks.length} links
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="text-gray-400 mb-2">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p className="text-gray-500 font-medium">No links detected yet</p>
              <p className="text-gray-400 text-sm mt-1">Configure your source and click "Detect Links"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkingConfiguration; 